import React, { useState, useMemo } from 'react';
import { useApp, convertUnit } from '../contexts/AppContext';
import { GitCompareArrows, Check, ChefHat, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { printReport } from '../utils/exportUtils';

const fmt = (val) => `$${(val || 0).toFixed(2)}`;
const fmtPct = (val) => `${(val || 0).toFixed(1)}%`;

export default function RecipeCompare() {
  const { recipes, ingredients } = useApp();
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);

  const toggle = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  // Enriched selected recipes with recalculated data
  const compared = useMemo(() => {
    return selected.map(id => {
      const r = recipes.find(rec => rec.id === id);
      if (!r) return null;

      let ingredientCost = 0;
      for (const ri of (r.recipeIngredients || [])) {
        const ing = ingredients.find(i => i.id === ri.ingredientId);
        if (!ing) continue;
        const converted = convertUnit(Number(ri.amount) || 0, ri.unit, ing.purchaseUnit);
        ingredientCost += converted * ing.costPerUsableUnit;
      }
      const packagingCost = Number(r.packagingCost) || 0;
      const totalMinutes = (Number(r.prepMinutes) || 0) + (Number(r.cookMinutes) || 0);
      const loadedRate = (Number(r.laborWage) || 0) * (1 + (Number(r.laborBurdenPercent) || 0) / 100);
      const laborCost = loadedRate * totalMinutes / 60;
      const totalCost = ingredientCost + packagingCost + laborCost;
      const menuPrice = Number(r.menuPrice) || 0;
      const grossProfit = menuPrice - totalCost;
      const grossMargin = menuPrice > 0 ? (grossProfit / menuPrice) * 100 : 0;
      const foodCostPct = menuPrice > 0 ? (ingredientCost / menuPrice) * 100 : 0;
      const primeCostPct = menuPrice > 0 ? ((ingredientCost + laborCost) / menuPrice) * 100 : 0;

      return {
        ...r, ingredientCost, packagingCost, laborCost, totalCost,
        grossProfit, grossMargin, foodCostPct, primeCostPct, totalMinutes,
      };
    }).filter(Boolean);
  }, [selected, recipes, ingredients]);

  const METRICS = [
    { label: 'Menu Price', key: 'menuPrice', format: fmt },
    { label: 'Ingredient Cost', key: 'ingredientCost', format: fmt },
    { label: 'Packaging Cost', key: 'packagingCost', format: fmt },
    { label: 'Labor Cost', key: 'laborCost', format: fmt },
    { label: 'Total Cost', key: 'totalCost', format: fmt, highlight: true },
    { label: 'Gross Profit', key: 'grossProfit', format: fmt, colorPositive: true },
    { label: 'Gross Margin %', key: 'grossMargin', format: fmtPct },
    { label: 'Food Cost %', key: 'foodCostPct', format: fmtPct, warnAbove: 35 },
    { label: 'Prime Cost %', key: 'primeCostPct', format: fmtPct, warnAbove: 65 },
    { label: 'Prep + Cook', key: 'totalMinutes', format: (v) => `${v} min` },
  ];

  // Find best value for each metric
  const bestValues = useMemo(() => {
    if (compared.length < 2) return {};
    const bests = {};
    METRICS.forEach(m => {
      const vals = compared.map(r => r[m.key]);
      if (m.key === 'grossProfit' || m.key === 'grossMargin' || m.key === 'menuPrice') {
        bests[m.key] = Math.max(...vals);
      } else {
        bests[m.key] = Math.min(...vals);
      }
    });
    return bests;
  }, [compared]);

  const handlePrint = () => {
    if (compared.length === 0) return;
    const html = `
      <h2>Comparing ${compared.length} Recipes</h2>
      <table>
        <tr><th>Metric</th>${compared.map(r => `<th style="text-align:right">${r.name}</th>`).join('')}</tr>
        ${METRICS.map(m => `<tr><td>${m.label}</td>${compared.map(r => `<td style="text-align:right">${m.format(r[m.key])}</td>`).join('')}</tr>`).join('')}
      </table>`;
    printReport('Recipe Comparison', html);
  };

  return (
    <div className="page-enter p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#2E7D32] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]">
            <GitCompareArrows className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="compare-title">
              Compare Recipes
            </h1>
            <p className="text-sm text-[#2A2A2A]/60">Side-by-side cost & profit comparison.</p>
          </div>
        </div>
        {compared.length >= 2 && (
          <button onClick={handlePrint} className="pizza-btn-secondary text-sm" data-testid="compare-print-btn">
            Print / PDF
          </button>
        )}
      </div>

      {recipes.length === 0 ? (
        <div className="pizza-card p-8 text-center" data-testid="compare-empty">
          <ChefHat className="w-12 h-12 text-[#2A2A2A]/20 mx-auto mb-3" />
          <h3 className="text-xl text-[#2A2A2A] mb-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>No Recipes to Compare</h3>
          <p className="text-sm text-[#2A2A2A]/50 mb-4">Build at least 2 recipes first, then come back to compare them.</p>
          <button onClick={() => navigate('/recipes')} className="pizza-btn text-sm" data-testid="compare-go-recipes-btn">
            <ArrowLeft className="w-4 h-4 inline mr-1" /> Go to Recipe Builder
          </button>
        </div>
      ) : (
        <>
          {/* Recipe selector */}
          <div className="pizza-card p-5 mb-6" data-testid="compare-selector">
            <h2 className="text-xl text-[#2A2A2A] mb-3" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Select Recipes to Compare
            </h2>
            <p className="text-xs text-[#2A2A2A]/50 mb-3">Pick 2 or more recipes. Tap to select.</p>
            <div className="flex flex-wrap gap-2">
              {recipes.map(r => {
                const isSelected = selected.includes(r.id);
                return (
                  <button
                    key={r.id}
                    onClick={() => toggle(r.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                      isSelected
                        ? 'bg-[#2E7D32] text-white border-[#2A2A2A] shadow-[3px_3px_0px_0px_rgba(42,42,42,1)]'
                        : 'bg-white text-[#2A2A2A] border-[#2A2A2A]/30 hover:border-[#2A2A2A]'
                    }`}
                    data-testid={`compare-toggle-${r.id}`}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5" />}
                    {r.name}
                    <span className="text-xs opacity-60">{r.category}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Comparison Table */}
          {compared.length >= 2 ? (
            <div className="overflow-x-auto" data-testid="compare-table-wrapper">
              <table className="pizza-table" data-testid="compare-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: '160px' }}>Metric</th>
                    {compared.map(r => (
                      <th key={r.id} style={{ minWidth: '140px', textAlign: 'right' }}>
                        {r.name}
                        <span className="block text-xs font-normal opacity-60">{r.category}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map(m => (
                    <tr key={m.key}>
                      <td className={`font-medium ${m.highlight ? 'bg-[#FFF8E7]' : ''}`}>{m.label}</td>
                      {compared.map(r => {
                        const val = r[m.key];
                        const isBest = compared.length >= 2 && val === bestValues[m.key];
                        let colorClass = '';
                        if (m.colorPositive) {
                          colorClass = val >= 0 ? 'text-[#2E7D32]' : 'text-[#E23D28]';
                        } else if (m.warnAbove && val > m.warnAbove) {
                          colorClass = 'text-[#E23D28]';
                        }
                        return (
                          <td key={r.id} className={`text-right font-medium ${m.highlight ? 'bg-[#FFF8E7]' : ''} ${colorClass}`}>
                            <span className={isBest ? 'inline-flex items-center gap-1' : ''}>
                              {m.format(val)}
                              {isBest && <span className="inline-block w-2 h-2 rounded-full bg-[#2E7D32]" title="Best" />}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-[#2A2A2A]/40 mt-2 flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-[#2E7D32]" /> = Best value in that row
              </p>
            </div>
          ) : selected.length === 1 ? (
            <div className="pizza-card p-6 text-center" data-testid="compare-need-more">
              <p className="text-[#2A2A2A]/60 text-sm">Select at least <strong>one more recipe</strong> to start comparing.</p>
            </div>
          ) : (
            <div className="pizza-card p-6 text-center" data-testid="compare-select-prompt">
              <p className="text-[#2A2A2A]/60 text-sm">Select 2 or more recipes above to see a side-by-side comparison.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
